/**
 * Warmoura Testimonials & UGC.
 *
 * The row is the browser's own scroll container, so a swipe is handled by the
 * platform and nothing here can cancel it. Script does four things: move the
 * scroll position for the arrows, read it back for the dots, start a video when
 * someone asks for one, and stop a video the moment its card is scrolled away
 * from — a testimonial talking to an empty screen is the thing to avoid.
 *
 * No video autoplays, and no video file is fetched until it is played: the
 * markup ships preload="none" behind a poster image.
 */
(() => {
  if (window.WarmouraUgcBound) return;
  window.WarmouraUgcBound = true;

  const setup = (section) => {
    if (section.dataset.ugcBound === 'true') return;
    const track = section.querySelector('[data-ugc-track]');
    if (!track) return;
    section.dataset.ugcBound = 'true';

    const cards = () => Array.from(track.querySelectorAll('[data-ugc-card]'));
    const prev = section.querySelector('[data-ugc-prev]');
    const next = section.querySelector('[data-ugc-next]');
    const dotsHost = section.querySelector('[data-ugc-dots]');

    // ---- carousel ---------------------------------------------------------
    const step = () => {
      const first = cards()[0];
      if (!first) return 0;
      const gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
      return Math.round(first.getBoundingClientRect().width + gap);
    };

    const activeIndex = () => {
      const origin = track.scrollLeft;
      let best = 0;
      let bestDistance = Infinity;
      cards().forEach((card, index) => {
        const distance = Math.abs(card.offsetLeft - track.offsetLeft - origin);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = index;
        }
      });
      return best;
    };

    // A flex row is as tall as its tallest card. On a phone, where one card is
    // read at a time, the row follows that card rather than reserving the
    // height of the longest one.
    const syncHeight = (index) => {
      if (!window.matchMedia('(max-width: 749px)').matches) {
        track.style.height = '';
        return;
      }
      const card = cards()[index];
      if (!card) return;
      const styles = getComputedStyle(track);
      const padding = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
      track.style.height = `${Math.round(card.getBoundingClientRect().height + padding)}px`;
    };

    // A video that has scrolled out of view is paused where it stands, so it
    // keeps its place and can be resumed rather than restarted.
    const pauseHidden = (index) => {
      cards().forEach((card, i) => {
        if (i === index) return;
        const video = card.querySelector('video');
        if (video && !video.paused) {
          video.pause();
          card.querySelector('[data-ugc-video]')?.classList.remove('is-playing');
        }
      });
    };

    const paint = () => {
      const index = activeIndex();
      Array.from(dotsHost?.children || []).forEach((dot, i) =>
        dot.classList.toggle('is-active', i === index)
      );
      if (prev) prev.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
      syncHeight(index);
      pauseHidden(index);
    };

    const buildDots = () => {
      if (!dotsHost) return;
      dotsHost.replaceChildren();
      cards().forEach((card, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'warmoura-ugc__dot';
        dot.setAttribute('aria-label', `Card ${index + 1}`);
        dot.addEventListener('click', () => {
          track.scrollTo({ left: card.offsetLeft - track.offsetLeft, behavior: 'smooth' });
        });
        dotsHost.appendChild(dot);
      });
      dotsHost.removeAttribute('aria-hidden');
    };

    let queued = false;
    track.addEventListener(
      'scroll',
      () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          paint();
        });
      },
      { passive: true }
    );

    prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    window.addEventListener('resize', paint, { passive: true });

    // ---- video ------------------------------------------------------------
    section.querySelectorAll('[data-ugc-video]').forEach((holder) => {
      const video = holder.querySelector('video');
      const play = holder.querySelector('[data-ugc-play]');
      if (!video || !play) return;

      // Sound is the customer's decision, and the file is only fetched now.
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.preload = 'none';
      video.controls = true;

      play.addEventListener('click', () => {
        // One at a time: starting this one stops whatever else was running.
        section.querySelectorAll('video').forEach((other) => {
          if (other !== video && !other.paused) {
            other.pause();
            other.closest('[data-ugc-video]')?.classList.remove('is-playing');
          }
        });
        holder.classList.add('is-playing');
        const started = video.play();
        if (started && typeof started.catch === 'function') {
          started.catch(() => {
            // Autoplay policies can refuse a programmatic play: hand the
            // customer the native controls rather than a dead card.
            holder.classList.add('is-playing');
          });
        }
      });

      // Returning to the poster when a video finishes makes replay obvious.
      video.addEventListener('ended', () => holder.classList.remove('is-playing'));
    });

    // ---- photo lightbox ---------------------------------------------------
    const dialog = section.querySelector('[data-ugc-lightbox]');
    const image = section.querySelector('[data-ugc-lightbox-image]');
    if (dialog && image && typeof dialog.showModal === 'function') {
      section.querySelectorAll('[data-ugc-photo]').forEach((button) => {
        button.addEventListener('click', () => {
          image.src = button.dataset.ugcPhoto;
          dialog.showModal();
        });
      });
      section.querySelector('[data-ugc-lightbox-close]')?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener('close', () => image.removeAttribute('src'));
    }

    buildDots();
    paint();
  };

  const init = (scope) => {
    (scope || document).querySelectorAll('[data-warmoura-ugc]').forEach(setup);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
